import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { HttpError, SLOT_REFUSAL } from '@vagusx/clients';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

interface UseRescheduleAppointmentResult {
  isRescheduling: boolean;
  reschedule: (id: string, newStartTime: string) => Promise<boolean>;
}

/**
 * Reschedule an appointment to a new start time (an in-place native EHR move,
 * ADR-092). On a slot-taken conflict the backend returns a failure; we surface a
 * re-offer toast so the caller can re-fetch open slots. Mirrors {@link useBooking}.
 */
export function useRescheduleAppointment(): UseRescheduleAppointmentResult {
  const queryClient = useQueryClient();
  const [isRescheduling, setIsRescheduling] = useState(false);

  const reschedule = async (id: string, newStartTime: string): Promise<boolean> => {
    setIsRescheduling(true);
    try {
      await getAppointmentClient().reschedule(id, { newStartTime });
      toast.success('Appointment rescheduled');
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
      return true;
    } catch (err) {
      toast.error(rescheduleFailureMessage(err));
      return false;
    } finally {
      setIsRescheduling(false);
    }
  };

  return { isRescheduling, reschedule };
}

/** What staff are told when a move fails. */
export function rescheduleFailureMessage(err: unknown): string {
  // Refused before the EHR was asked, for a reason staff can act on (ADR-313):
  // the time is open, but not for this appointment's type. Say exactly that.
  if (err instanceof HttpError && err.code === SLOT_REFUSAL.SLOT_TYPE_MISMATCH) {
    return err.message;
  }
  const msg = err instanceof HttpError || err instanceof Error ? err.message.toLowerCase() : '';
  if (msg.includes('available') || msg.includes('slot') || msg.includes('conflict')) {
    return 'That time is no longer available. Please pick another slot.';
  }
  return 'Failed to reschedule appointment. Please try again.';
}
